import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Modal from '../components/Modal';
import Avatar from '../components/Avatar';
import ShareModal from '../components/ShareModal';
import { projectsApi, filesApi, foldersApi, usersApi } from '../utils/api';
import { formatFileSize, formatDate, formatRelativeTime, getFileIcon, getFileTypeLabel, downloadBlob, MAX_FILE_SIZE } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Search, Filter, Upload, Download, Trash2, Archive,
  ArchiveRestore, FileText, Table, Image, File, MoreVertical,
  Settings, Users, Eye, Edit2, Folder, FolderPlus, ChevronRight,
  AlertTriangle, X, Check, Square, CheckSquare, FolderOpen, Share2, Video, Box
} from 'lucide-react';

const ProjectPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Folder navigation
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);

  // Multi-select
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [selectMode, setSelectMode] = useState(false);

  const [search, setSearch] = useState('');
  const [selectedExtension, setSelectedExtension] = useState('');
  const [showArchived, setShowArchived] = useState('only_active');

  const [showSettingsModal, setShowSettingsModal] = useState(searchParams.get('settings') === 'true');
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareFile, setShareFile] = useState(null);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    loadProject();
    loadFiles();
    loadFolders();
    loadExtensions();
  }, [projectId]);

  useEffect(() => {
    loadFiles();
    loadFolders();
    setSelectedFiles([]);
    setSelectedFolders([]);
  }, [currentFolderId]);

  useEffect(() => {
    loadFiles();
  }, [search, selectedExtension, showArchived]);

  const loadProject = async () => {
    try {
      const response = await projectsApi.getById(projectId);
      setProject(response.data);
    } catch (err) {
      console.error('Load project error:', err);
      navigate('/');
    }
  };

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await filesApi.getByProject(projectId, {
        search,
        extension: selectedExtension,
        showArchived,
        folderId: currentFolderId || ''
      });
      setFiles(response.data);
    } catch (err) {
      console.error('Load files error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      const response = await foldersApi.getByProject(projectId, currentFolderId);
      setFolders(response.data);
    } catch (err) {
      console.error('Load folders error:', err);
    }
  };

  const loadFolderPath = async (folderId) => {
    if (!folderId) {
      setFolderPath([]);
      return;
    }
    try {
      const response = await foldersApi.getById(folderId);
      setFolderPath(response.data.path || []);
    } catch (err) {
      console.error('Load folder path error:', err);
    }
  };

  const navigateToFolder = (folderId) => {
    setCurrentFolderId(folderId);
    loadFolderPath(folderId);
  };

  const loadExtensions = async () => {
    try {
      const response = await filesApi.getExtensions(projectId);
      setExtensions(response.data);
    } catch (err) {
      console.error('Load extensions error:', err);
    }
  };

  const uploadFile = async (file) => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File "${file.name}" is too large. Maximum size is 800MB.`);
      setTimeout(() => setUploadError(null), 5000);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadError(null);

      await filesApi.upload(projectId, file, currentFolderId, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(progress);
      });

      loadFiles();
      loadExtensions();
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err.response?.data?.error || 'Failed to upload file');
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    await uploadFile(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (canWrite) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!canWrite) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Upload files one by one
    for (const file of files) {
      await uploadFile(file);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await foldersApi.create(projectId, newFolderName.trim(), currentFolderId);
      setShowCreateFolderModal(false);
      setNewFolderName('');
      loadFolders();
    } catch (err) {
      console.error('Create folder error:', err);
    }
  };

  // Multi-select handlers
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedFiles([]);
    setSelectedFolders([]);
  };

  const toggleFileSelection = (fileId, e) => {
    e.stopPropagation();
    setSelectedFiles(prev =>
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  };

  const toggleFolderSelection = (folderId, e) => {
    e.stopPropagation();
    setSelectedFolders(prev =>
      prev.includes(folderId) ? prev.filter(id => id !== folderId) : [...prev, folderId]
    );
  };

  const selectAll = () => {
    setSelectedFiles(files.map(f => f.id));
    setSelectedFolders(folders.map(f => f.id));
  };

  const deselectAll = () => {
    setSelectedFiles([]);
    setSelectedFolders([]);
  };

  // Bulk operations
  const handleBulkDelete = async () => {
    try {
      if (selectedFiles.length > 0) {
        await filesApi.bulkDelete(selectedFiles, projectId);
      }
      if (selectedFolders.length > 0) {
        await foldersApi.bulkDelete(selectedFolders, projectId);
      }
      setShowBulkDeleteModal(false);
      setSelectedFiles([]);
      setSelectedFolders([]);
      setSelectMode(false);
      loadFiles();
      loadFolders();
      loadExtensions();
    } catch (err) {
      console.error('Bulk delete error:', err);
    }
  };

  const handleBulkArchive = async (archived) => {
    try {
      if (selectedFiles.length > 0) {
        await filesApi.bulkArchive(selectedFiles, archived, projectId);
      }
      setSelectedFiles([]);
      setSelectMode(false);
      loadFiles();
      loadExtensions();
    } catch (err) {
      console.error('Bulk archive error:', err);
    }
  };

  const handleDownload = async (file, e) => {
    e.stopPropagation();
    try {
      const response = await filesApi.download(file.id);
      downloadBlob(response.data, file.original_name);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handleArchive = async (file, archived, e) => {
    e.stopPropagation();
    try {
      await filesApi.archive(file.id, archived);
      loadFiles();
      loadExtensions();
    } catch (err) {
      console.error('Archive error:', err);
    }
  };

  const handleDeleteFile = async () => {
    if (!showDeleteFileModal) return;
    try {
      await filesApi.delete(showDeleteFileModal.id);
      setShowDeleteFileModal(null);
      loadFiles();
      loadExtensions();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const canWrite = project?.permissions?.can_write || project?.permissions?.full_control || project?.is_owner;
  const canDownload = project?.permissions?.can_download || project?.permissions?.full_control || project?.is_owner;
  const canDelete = project?.permissions?.can_delete || project?.permissions?.full_control || project?.is_owner;

  const getFileIconComponent = (extension, mimeType) => {
    const iconType = getFileIcon(extension, mimeType);
    switch (iconType) {
      case 'file-text': return FileText;
      case 'table': return Table;
      case 'image': return Image;
      case 'video': return Video;
      case 'model': return Box;
      default: return File;
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50 dark:bg-gray-900"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Header />

      {/* Drag overlay */}
      {isDragging && canWrite && (
        <div className="fixed inset-0 bg-primary-500/20 border-4 border-dashed border-primary-500 z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl text-center">
            <Upload className="w-16 h-16 text-primary-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Drop files here
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Files will be uploaded to the current folder
            </p>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button and project header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to projects
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
                <Folder className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {project?.name}
                </h1>
                {project?.description && (
                  <p className="text-gray-500 dark:text-gray-400 mt-1">
                    {project.description}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Select mode toggle */}
              <button
                onClick={toggleSelectMode}
                className={`btn ${selectMode ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
              >
                {selectMode ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                <span className="hidden sm:inline">Select</span>
              </button>

              {project?.is_owner && (
                <button
                  onClick={() => setShowSettingsModal(true)}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <Settings className="w-5 h-5" />
                  <span className="hidden sm:inline">Settings</span>
                </button>
              )}
              {canWrite && (
                <>
                  <button
                    onClick={() => setShowCreateFolderModal(true)}
                    className="btn btn-secondary flex items-center gap-2"
                  >
                    <FolderPlus className="w-5 h-5" />
                    <span className="hidden sm:inline">New folder</span>
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.docx,.doc,.xlsx,.xls,.jpg,.jpeg,.png,.gif,.webp,.svg,.mp4,.webm,.ogg,.mov,.avi,.mkv,.stl,.obj,.3mf"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    <Upload className="w-5 h-5" />
                    <span className="hidden sm:inline">Upload</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Upload error */}
        {uploadError && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{uploadError}</p>
            <button
              onClick={() => setUploadError(null)}
              className="ml-auto p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Upload progress */}
        {uploading && (
          <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Uploading...
              </span>
              <span className="text-sm text-gray-500">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Breadcrumb navigation */}
        {(currentFolderId || folderPath.length > 0) && (
          <div className="flex items-center gap-2 mb-4 text-sm">
            <button
              onClick={() => navigateToFolder(null)}
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              Root folder
            </button>
            {folderPath.map((folder, index) => (
              <React.Fragment key={folder.id}>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <button
                  onClick={() => navigateToFolder(folder.id)}
                  className={index === folderPath.length - 1
                    ? 'text-gray-900 dark:text-white font-medium'
                    : 'text-primary-600 dark:text-primary-400 hover:underline'
                  }
                >
                  {folder.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Bulk action bar */}
        {selectMode && (selectedFiles.length > 0 || selectedFolders.length > 0) && (
          <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                {selectedFiles.length + selectedFolders.length} items selected
              </span>
              <button onClick={selectAll} className="text-sm text-primary-600 hover:underline">
                Select all
              </button>
              <button onClick={deselectAll} className="text-sm text-primary-600 hover:underline">
                Deselect all
              </button>
            </div>
            <div className="flex items-center gap-2">
              {selectedFiles.length > 0 && canWrite && (
                <button
                  onClick={() => handleBulkArchive(true)}
                  className="btn btn-secondary btn-sm flex items-center gap-1"
                >
                  <Archive className="w-4 h-4" />
                  Archive
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => setShowBulkDeleteModal(true)}
                  className="btn btn-danger btn-sm flex items-center gap-1"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>

          <select
            value={selectedExtension}
            onChange={(e) => setSelectedExtension(e.target.value)}
            className="input w-full sm:w-40"
          >
            <option value="">All types</option>
            {extensions.map((ext) => (
              <option key={ext.file_extension} value={ext.file_extension}>
                .{ext.file_extension} ({ext.count})
              </option>
            ))}
          </select>

          <select
            value={showArchived}
            onChange={(e) => setShowArchived(e.target.value)}
            className="input w-full sm:w-40"
          >
            <option value="only_active">Active files</option>
            <option value="true">Archived files</option>
            <option value="all">All files</option>
          </select>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        )}

        {/* Empty state */}
        {!loading && files.length === 0 && folders.length === 0 && (
          <div className="text-center py-12 card p-8">
            <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {search || selectedExtension ? 'No results' : 'Empty folder'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {search || selectedExtension
                ? 'Try different search criteria'
                : 'Create folders or upload files'}
            </p>
            {canWrite && !search && !selectedExtension && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setShowCreateFolderModal(true)}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <FolderPlus className="w-5 h-5" />
                  New folder
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Upload className="w-5 h-5" />
                  Upload file
                </button>
              </div>
            )}
          </div>
        )}

        {/* Folder and file list */}
        {!loading && (folders.length > 0 || files.length > 0) && (
          <div className="space-y-3">
            {/* Folders */}
            {folders.map((folder) => (
              <div
                key={`folder-${folder.id}`}
                onClick={() => !selectMode && navigateToFolder(folder.id)}
                className={`card p-4 hover:shadow-lg transition-all cursor-pointer ${
                  selectedFolders.includes(folder.id) ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Select checkbox */}
                  {selectMode && (
                    <button
                      onClick={(e) => toggleFolderSelection(folder.id, e)}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      {selectedFolders.includes(folder.id) ? (
                        <CheckSquare className="w-5 h-5 text-primary-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  )}

                  {/* Folder icon */}
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-yellow-100 dark:bg-yellow-900/30">
                    <FolderOpen className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                  </div>

                  {/* Folder info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-white truncate">
                      {folder.name}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                      <span>Folder</span>
                      <span className="hidden sm:inline">
                        {formatRelativeTime(folder.created_at)}
                      </span>
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))}

            {/* Files */}
            {files.map((file) => {
              const IconComponent = getFileIconComponent(file.file_extension, file.mime_type);
              return (
                <div
                  key={file.id}
                  onClick={() => !selectMode && navigate(`/project/${projectId}/file/${file.id}`)}
                  className={`card p-4 hover:shadow-lg transition-all cursor-pointer ${
                    file.is_archived ? 'opacity-60' : ''
                  } ${selectedFiles.includes(file.id) ? 'ring-2 ring-primary-500' : ''}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Select checkbox */}
                    {selectMode && (
                      <button
                        onClick={(e) => toggleFileSelection(file.id, e)}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        {selectedFiles.includes(file.id) ? (
                          <CheckSquare className="w-5 h-5 text-primary-600" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    )}

                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      file.is_archived
                        ? 'bg-gray-200 dark:bg-gray-700'
                        : 'bg-primary-100 dark:bg-primary-900/30'
                    }`}>
                      <IconComponent className={`w-6 h-6 ${
                        file.is_archived
                          ? 'text-gray-500 dark:text-gray-400'
                          : 'text-primary-600 dark:text-primary-400'
                      }`} />
                    </div>

                    {/* File info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 dark:text-white truncate">
                          {file.original_name}
                        </h3>
                        {file.is_archived && (
                          <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                            Archived
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                        <span>{getFileTypeLabel(file.file_extension)}</span>
                        <span>{formatFileSize(file.file_size)}</span>
                        <span className="hidden sm:inline">
                          {formatRelativeTime(file.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShareFile(file);
                          setShowShareModal(true);
                        }}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Share"
                      >
                        <Share2 className="w-5 h-5" />
                      </button>

                      {canDownload && (
                        <button
                          onClick={(e) => handleDownload(file, e)}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                      )}

                      {canWrite && (
                        <button
                          onClick={(e) => handleArchive(file, !file.is_archived, e)}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title={file.is_archived ? 'Restore' : 'Archive'}
                        >
                          {file.is_archived ? (
                            <ArchiveRestore className="w-5 h-5" />
                          ) : (
                            <Archive className="w-5 h-5" />
                          )}
                        </button>
                      )}

                      {canDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDeleteFileModal(file);
                          }}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Settings Modal */}
      <ProjectSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        project={project}
        onUpdate={loadProject}
      />

      {/* Delete File Modal */}
      <Modal
        isOpen={!!showDeleteFileModal}
        onClose={() => setShowDeleteFileModal(null)}
        title="Delete file"
      >
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-200">
            This action cannot be undone!
          </p>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Are you sure you want to delete this file?
          <strong className="block text-gray-900 dark:text-white mt-1">
            {showDeleteFileModal?.original_name}
          </strong>
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setShowDeleteFileModal(null)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button onClick={handleDeleteFile} className="btn btn-danger">
            Delete
          </button>
        </div>
      </Modal>

      {/* Create Folder Modal */}
      <Modal
        isOpen={showCreateFolderModal}
        onClose={() => {
          setShowCreateFolderModal(false);
          setNewFolderName('');
        }}
        title="Create new folder"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Folder name
            </label>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name..."
              className="input"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowCreateFolderModal(false);
                setNewFolderName('');
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim()}
              className="btn btn-primary"
            >
              Create
            </button>
          </div>
        </div>
      </Modal>

      {/* Bulk Delete Modal */}
      <Modal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        title="Bulk delete"
      >
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-200">
            This action cannot be undone!
          </p>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Are you sure you want to delete the selected items?
          <strong className="block text-gray-900 dark:text-white mt-1">
            {selectedFiles.length} files and {selectedFolders.length} folders
          </strong>
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setShowBulkDeleteModal(false)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button onClick={handleBulkDelete} className="btn btn-danger">
            Delete
          </button>
        </div>
      </Modal>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShareFile(null);
        }}
        file={shareFile}
      />
    </div>
  );
};

// Project Settings Modal
const ProjectSettingsModal = ({ isOpen, onClose, project, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const [permissions, setPermissions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [newPermission, setNewPermission] = useState({
    canRead: true,
    canWrite: false,
    canDownload: false,
    canDelete: false,
    fullControl: false
  });

  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description || '');
    }
  }, [project]);

  useEffect(() => {
    if (isOpen && project?.is_owner) {
      loadPermissions();
      loadUsers();
    }
  }, [isOpen, project]);

  const loadPermissions = async () => {
    try {
      const response = await projectsApi.getPermissions(project.id);
      setPermissions(response.data);
    } catch (err) {
      console.error('Load permissions error:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await usersApi.getAll();
      setAllUsers(response.data);
    } catch (err) {
      console.error('Load users error:', err);
    }
  };

  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await projectsApi.update(project.id, { name, description });
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddPermission = async () => {
    if (!selectedUser) return;
    try {
      await projectsApi.setPermission(project.id, {
        userId: selectedUser,
        ...newPermission
      });
      setSelectedUser('');
      setNewPermission({
        canRead: true,
        canWrite: false,
        canDownload: false,
        canDelete: false,
        fullControl: false
      });
      loadPermissions();
    } catch (err) {
      console.error('Add permission error:', err);
    }
  };

  const handleRemovePermission = async (userId) => {
    try {
      await projectsApi.removePermission(project.id, userId);
      loadPermissions();
    } catch (err) {
      console.error('Remove permission error:', err);
    }
  };

  const handleUpdatePermission = async (userId, perm) => {
    try {
      await projectsApi.setPermission(project.id, {
        userId,
        canRead: perm.can_read,
        canWrite: perm.can_write,
        canDownload: perm.can_download,
        canDelete: perm.can_delete,
        fullControl: perm.full_control
      });
      loadPermissions();
    } catch (err) {
      console.error('Update permission error:', err);
    }
  };

  const availableUsers = allUsers.filter(
    (u) => u.id !== project?.owner_id && !permissions.some((p) => p.user_id === u.id)
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Project settings" size="lg">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'general'
              ? 'border-primary-500 text-primary-600 dark:text-primary-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          General
        </button>
        <button
          onClick={() => setActiveTab('permissions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'permissions'
              ? 'border-primary-500 text-primary-600 dark:text-primary-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Permissions
        </button>
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <form onSubmit={handleSaveGeneral}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Project name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="input min-h-[100px]"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn btn-primary">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <div>
          {/* Add new permission */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg mb-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Add user
            </h4>
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="input flex-1"
              >
                <option value="">Select user...</option>
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
              <button
                onClick={handleAddPermission}
                disabled={!selectedUser}
                className="btn btn-primary"
              >
                Add
              </button>
            </div>

            {/* Permission checkboxes for new user */}
            {selectedUser && (
              <div className="flex flex-wrap gap-4 mt-3">
                {['canRead', 'canWrite', 'canDownload', 'canDelete', 'fullControl'].map((key) => (
                  <label key={key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={newPermission[key]}
                      onChange={(e) => setNewPermission({ ...newPermission, [key]: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-gray-700 dark:text-gray-300">
                      {key === 'canRead' && 'Read'}
                      {key === 'canWrite' && 'Write'}
                      {key === 'canDownload' && 'Download'}
                      {key === 'canDelete' && 'Delete'}
                      {key === 'fullControl' && 'Full control'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Existing permissions */}
          <div className="space-y-3">
            {permissions.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                No users shared yet
              </p>
            ) : (
              permissions.map((perm) => (
                <div
                  key={perm.user_id}
                  className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                >
                  <Avatar name={perm.name} userId={perm.user_id} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      {perm.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{perm.email}</p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {['can_read', 'can_write', 'can_download', 'can_delete', 'full_control'].map((key) => (
                      <button
                        key={key}
                        onClick={() => handleUpdatePermission(perm.user_id, { ...perm, [key]: !perm[key] })}
                        className={`px-2 py-1 text-xs rounded ${
                          perm[key]
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                            : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {key === 'can_read' && 'Read'}
                        {key === 'can_write' && 'Write'}
                        {key === 'can_download' && 'Dnld'}
                        {key === 'can_delete' && 'Del'}
                        {key === 'full_control' && 'Full'}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => handleRemovePermission(perm.user_id)}
                    className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end mt-6">
            <button onClick={onClose} className="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ProjectPage;
